import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Input, Spin, Typography } from 'antd';
import { PaperPlaneRight, X } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { callGeminiChat, type GeminiChatTurn } from '../../../../services/geminiService';
import { postAssistantTelemetry } from '../../../../services/assistantTelemetryService';
import { buildOpportunityAssistantSystemInstruction } from './opportunityAssistantPrompt';
import { buildOpportunityAssistantSuggestions } from './opportunityAssistantSuggestions';
import { computeCommerceGuide } from './commerceGuideLogic';
import { OBD_OPPORTUNITY_ASSISTANT_DRAWER_Z } from './oneButtonOverlayZ';
import './oneButton-css/OpportunityAssistantDrawer.css';

const { Text } = Typography;

const MAX_HISTORY_TURNS = 10;

export interface OpportunityAssistantDrawerProps {
    open: boolean;
    onClose: () => void;
}

type ChatMsg = { role: 'user' | 'assistant'; text: string };

export const OpportunityAssistantDrawer: React.FC<OpportunityAssistantDrawerProps> = ({ open, onClose }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const listEndRef = useRef<HTMLDivElement>(null);
    const messagesRef = useRef<ChatMsg[]>([]);
    messagesRef.current = messages;

    const scrollToBottom = useCallback(() => {
        listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, []);

    useEffect(() => {
        if (open) scrollToBottom();
    }, [open, messages, loading, scrollToBottom]);

    const financeContextKey = useOneButtonStore(
        (s) =>
            `${s.selectedFinanceCells.size}:${Object.keys(s.financePartnerSyncStatus).join(',')}:${s.dashboardQuotesSummaryLines?.selectedQuoteLine ?? ''}`
    );

    const suggestions = useMemo(
        () => (open ? buildOpportunityAssistantSuggestions() : []),
        [open, financeContextKey]
    );

    const sendMessage = useCallback(
        async (textOverride?: string) => {
            const text = (textOverride ?? input).trim();
            if (!text || loading) return;
            const apiKey = (import.meta as any).env?.VITE_GEMINI_API_KEY as string | undefined;
            if (!apiKey) {
                setError('VITE_GEMINI_API_KEY is not set. Add it to your environment to use the assistant.');
                return;
            }
            setError(null);
            if (!textOverride) setInput('');
            setMessages((m) => [...m, { role: 'user', text }]);
            setLoading(true);
            try {
                const history: GeminiChatTurn[] = messages.slice(-MAX_HISTORY_TURNS * 2).map((msg) => ({
                    role: msg.role === 'user' ? ('user' as const) : ('model' as const),
                    text: msg.text,
                }));
                const systemInstruction = buildOpportunityAssistantSystemInstruction();
                const reply = await callGeminiChat(apiKey, systemInstruction, history, text, {
                    temperature: 0.35,
                    maxOutputTokens: 4096,
                });
                setMessages((m) => [...m, { role: 'assistant', text: reply }]);
            } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                setError(msg);
                setMessages((m) => [
                    ...m,
                    { role: 'assistant', text: `Sorry — I could not get a response. (${msg})` },
                ]);
            } finally {
                setLoading(false);
            }
        },
        [input, loading, messages]
    );

    const send = useCallback(() => void sendMessage(), [sendMessage]);

    const handleAfterOpenChange = useCallback((isOpen: boolean) => {
        if (isOpen) return;
        const s = useOneButtonStore.getState();
        const guide = computeCommerceGuide({
            selectedFinanceCells: s.selectedFinanceCells,
            financePartnerSyncStatus: s.financePartnerSyncStatus,
            opportunityDetails: s.opportunityDetails,
            dashboardQuotesSummaryLines: s.dashboardQuotesSummaryLines,
            changeOrderDetails: s.changeOrderDetails,
        });
        const msgs = messagesRef.current;
        postAssistantTelemetry({
            event: 'assistant_drawer_closed',
            guideSection: guide.section,
            userTurns: msgs.filter((m) => m.role === 'user').length,
            assistantTurns: msgs.filter((m) => m.role === 'assistant').length,
        });
    }, []);

    const footer = (
        <div className={`obd-assistant-drawer__composer obd-theme-${theme}`}>
            <Input.TextArea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about this opportunity, financing, sync, quotes…"
                autoSize={{ minRows: 2, maxRows: 5 }}
                onPressEnter={(e) => {
                    if (!e.shiftKey) {
                        e.preventDefault();
                        void send();
                    }
                }}
                disabled={loading}
                className="obd-assistant-drawer__input"
            />
            <Button
                type="primary"
                icon={<PaperPlaneRight size={18} weight="bold" />}
                onClick={() => void send()}
                loading={loading}
                disabled={!input.trim()}
                className="obd-assistant-drawer__send"
            >
                Send
            </Button>
        </div>
    );

    return (
        <Drawer
            title={
                <div className="obd-assistant-drawer__title">
                    <span className="obd-assistant-drawer__title-name">Sunny</span>
                    <Text type="secondary" className="obd-assistant-drawer__title-sub">
                        Opportunity assistant
                    </Text>
                </div>
            }
            placement="right"
            width={Math.min(440, typeof window !== 'undefined' ? window.innerWidth - 24 : 440)}
            open={open}
            onClose={onClose}
            afterOpenChange={handleAfterOpenChange}
            closeIcon={<X size={20} weight="bold" />}
            destroyOnHidden
            zIndex={OBD_OPPORTUNITY_ASSISTANT_DRAWER_Z}
            className={`obd-assistant-drawer obd-theme-${theme}`}
            styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
            footer={footer}
        >
            <div className="obd-assistant-drawer__intro">
                <Text type="secondary">
                    Answers use your current opportunity, financing selection, partner sync state, and dashboard summaries.
                    Not legal or financial advice.
                </Text>
            </div>
            {error && (
                <div className="obd-assistant-drawer__error" role="alert">
                    {error}
                </div>
            )}
            <div className="obd-assistant-drawer__messages">
                <div className="obd-assistant-drawer__thread">
                    {messages.length === 0 && !loading && (
                        <div className="obd-assistant-drawer__bubble obd-assistant-drawer__bubble--assistant obd-assistant-drawer__bubble--welcome">
                            Hi — I&apos;m Sunny. I can help with next steps, financing, partner sync,
                            products, quotes, and agreements for this opportunity. Pick a topic or type your
                            own question below.
                        </div>
                    )}
                    {messages.map((msg, i) => (
                        <div
                            key={i}
                            className={`obd-assistant-drawer__bubble obd-assistant-drawer__bubble--${msg.role}`}
                        >
                            {msg.text}
                        </div>
                    ))}
                    {loading && (
                        <div className="obd-assistant-drawer__typing">
                            <Spin size="small" />
                            <span>Sunny is thinking…</span>
                        </div>
                    )}
                    <div ref={listEndRef} />
                </div>

                {suggestions.length > 0 ? (
                    <div className="obd-assistant-drawer__suggestions-panel">
                        <span className="obd-assistant-drawer__suggestions-label">Quick questions</span>
                        <div
                            className="obd-assistant-drawer__suggestions"
                            role="group"
                            aria-label="Suggested questions"
                        >
                            {suggestions.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="obd-assistant-drawer__suggestion"
                                    disabled={loading}
                                    onClick={() => void sendMessage(item.prompt)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        </Drawer>
    );
};

export default OpportunityAssistantDrawer;
